// src/pages/Health/HealthPage.jsx
// АРМ модуля «Здравоохранение» — ведение план/факт показателей (UC.05.02).
// Стек: React + Ant Design v4. Основание: СТПО 3.1.5, таблицы 28–30, RQ.09.01–RQ.09.08.
// Только создание (create-only); правка/удаление не предусмотрены.
//
// Подписи интерфейса — из общего i18next-словаря, префикс health.* (RU/KK).
// Значения справочника (названия/единицы/направление) — из /api/health/meta,
// не переводятся; язык для выбора name_kk/ru и числовой локали — lang ниже.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Select, Button, Input, InputNumber, Tag, Alert, notification } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { fetchHealthMeta, fetchHealthRows, createHealthRow, ApiError } from './api';
import BasicLayout from '../../components/layout/BasicLayout';
import './HealthPage.css';

const { Option } = Select;
const { TextArea } = Input;

const YEARS = [];
for (let y = 2023; y <= 2030; y += 1) YEARS.push(y);

const EMPTY_FORM = {
  indicator_code: undefined,
  period_type: 'year',
  year: undefined,
  quarter: undefined,
  plan: '',
  fact: '',
  note: '',
};

export default function HealthPage() {
  const { t, i18n } = useTranslation();

  const lang = (i18n.language || '').toLowerCase().startsWith('ru') ? 'ru' : 'kk';

  // Справочник
  const [indicators, setIndicators] = useState([]);
  const byCode = useMemo(() => {
    const m = {};
    indicators.forEach((i) => {
      m[i.code] = i;
    });
    return m;
  }, [indicators]);

  // Перечень
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Фильтры перечня (клиентские разрезы, на модель не влияют)
  const [fltPeriod, setFltPeriod] = useState('');
  const [fltIndicator, setFltIndicator] = useState('');

  // Модальная форма
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({}); // { field: 'reqMsg' | 'numMsg' | 'dupMsg' | текст }

  /* -------------------------------- Загрузка -------------------------------- */
  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    Promise.all([fetchHealthMeta(), fetchHealthRows()])
      .then(([meta, list]) => {
        const inds = (meta && meta.indicators ? meta.indicators.slice() : []).sort(
          (a, b) => a.sort_order - b.sort_order
        );
        setIndicators(inds);
        setRows(Array.isArray(list && list.rows) ? list.rows : []);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* --------------------------- Локализация значений -------------------------- */
  const indName = useCallback(
    (code) => {
      const i = byCode[code];
      return i ? (lang === 'kk' ? i.name_kk : i.name_ru) : '';
    },
    [byCode, lang]
  );

  const indUnit = useCallback(
    (code) => {
      const i = byCode[code];
      return i ? (lang === 'kk' ? i.unit_kk : i.unit_ru) : '';
    },
    [byCode, lang]
  );

  const dirLabel = useCallback(
    (code) => {
      const i = byCode[code];
      if (!i) return '';
      return i.direction === 'higher_better'
        ? t('health.dirHigherBetter')
        : t('health.dirLowerBetter');
    },
    [byCode, t]
  );

  const quarterLabel = useCallback((q) => t(`health.q${q}`), [t]);

  const periodLabel = useCallback(
    (r) => {
      if (r.period_type === 'year') return `${r.year} — ${t('health.lblYear')}`;
      return `${r.year} — ${quarterLabel(r.quarter)}`;
    },
    [t, quarterLabel]
  );

  const periodKey = (r) =>
    r.period_type === 'year' ? `${r.year}-year` : `${r.year}-q${r.quarter}`;

  const fmtNum = useCallback(
    (n) =>
      n == null
        ? null
        : Number(n).toLocaleString(lang === 'kk' ? 'kk-KZ' : 'ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
    [lang]
  );

  /* -------------------------------- Фильтры --------------------------------- */
  const periodOptions = useMemo(() => {
    const seen = new Set();
    const list = [];
    rows.forEach((r) => {
      const k = periodKey(r);
      if (!seen.has(k)) {
        seen.add(k);
        list.push(r);
      }
    });
    list.sort((a, b) => a.year * 10 + (a.quarter || 0) - (b.year * 10 + (b.quarter || 0)));
    return list.map((r) => ({ key: periodKey(r), label: periodLabel(r) }));
  }, [rows, periodLabel]);

  const view = useMemo(
    () =>
      rows.filter(
        (r) =>
          (!fltPeriod || periodKey(r) === fltPeriod) &&
          (!fltIndicator || r.indicator_code === fltIndicator)
      ),
    [rows, fltPeriod, fltIndicator]
  );

  /* -------------------------------- Колонки --------------------------------- */
  const columns = [
    {
      title: t('health.colInd'),
      key: 'indicator',
      width: '46%',
      render: (_, r) => (
        <div>
          <div>{indName(r.indicator_code)}</div>
          <div className="hp-sub">{indUnit(r.indicator_code)}</div>
        </div>
      ),
    },
    {
      title: t('health.colPeriod'),
      key: 'period',
      width: '22%',
      render: (_, r) => (
        <Tag className={`hp-pill${r.period_type === 'quarter' ? ' hp-pill--q' : ''}`}>
          {periodLabel(r)}
        </Tag>
      ),
    },
    {
      title: t('health.colPlan'),
      key: 'plan',
      width: '16%',
      align: 'right',
      render: (_, r) => <span className="hp-num">{fmtNum(r.plan)}</span>,
    },
    {
      title: t('health.colFact'),
      key: 'fact',
      width: '16%',
      align: 'right',
      render: (_, r) => <span className="hp-num">{fmtNum(r.fact)}</span>,
    },
  ];

  /* ------------------------------ Форма: helpers ---------------------------- */
  const patch = (next) => setForm((prev) => ({ ...prev, ...next }));
  const clearError = (field) =>
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const cp = { ...prev };
      delete cp[field];
      return cp;
    });

  const openModal = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setModalOpen(true);
  };
  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false); // АС1 — закрытие без сохранения
  };

  const onIndicatorChange = (code) => {
    patch({ indicator_code: code });
    clearError('indicator_code');
    clearError('dup');
  };
  const onTypeChange = (pt) => {
    // Смена «Квартал»→«Год»: квартал сбрасывается и не отправляется
    patch({ period_type: pt, quarter: pt === 'year' ? undefined : form.quarter });
    clearError('quarter');
    clearError('dup');
  };

  // Число (10,2), ≥ 0: до 8 знаков целой части + до 2 дробных; 0 допустим
  const numOk = (raw) => {
    if (raw === '' || raw == null) return false;
    const s = String(raw).replace(',', '.').trim();
    if (!/^\d{1,8}(\.\d{1,2})?$/.test(s)) return false;
    const n = Number(s);
    return Number.isFinite(n) && n >= 0;
  };

  // АС2 — клиентская проверка обязательных полей и формата
  const validate = () => {
    const e = {};
    if (!form.indicator_code) e.indicator_code = 'reqMsg';
    if (!form.year) e.year = 'reqMsg';
    if (form.period_type === 'quarter' && !form.quarter) e.quarter = 'reqMsg';
    if (String(form.plan).trim() === '') e.plan = 'reqMsg';
    else if (!numOk(form.plan)) e.plan = 'numMsg';
    if (String(form.fact).trim() === '') e.fact = 'reqMsg';
    else if (!numOk(form.fact)) e.fact = 'numMsg';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => ({
    indicator_code: form.indicator_code,
    period_type: form.period_type,
    year: Number(form.year),
    quarter: form.period_type === 'quarter' ? Number(form.quarter) : null,
    plan: Number(String(form.plan).replace(',', '.')),
    fact: Number(String(form.fact).replace(',', '.')),
    note: form.note && form.note.trim() ? form.note.trim() : null,
  });

  const save = async () => {
    if (submitting) return;
    if (!validate()) {
      scrollToFirstError();
      return;
    }
    setSubmitting(true);
    try {
      const created = await createHealthRow(buildPayload());
      setRows((prev) => [created, ...prev]);
      setFltPeriod('');
      setFltIndicator('');
      setModalOpen(false);
      notification.success({
        message: t('health.toastOk'),
        description: t('health.toastSub'),
        placement: 'bottomRight',
      });
    } catch (err) {
      handleSaveError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveError = (err) => {
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 409) {
      // АС3 — дубль периода: форму не закрываем
      setErrors((prev) => ({ ...prev, dup: 'dupMsg' }));
      return;
    }
    if (status === 422 && err.body && err.body.fields) {
      const fields = err.body.fields;
      const names = Array.isArray(fields) ? fields : Object.keys(fields);
      const e = {};
      names.forEach((f) => {
        e[f] = !Array.isArray(fields) && fields[f] ? fields[f] : 'reqMsg';
      });
      setErrors(e);
      scrollToFirstError();
      return;
    }
    notification.error({ message: t('health.saveError'), placement: 'bottomRight' });
  };

  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector('.hp-field--err');
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 0);
  };

  // Текст сообщения об ошибке: ключи-словари → перевод; серверный текст — как есть
  const errText = (field) => {
    const v = errors[field];
    if (!v) return '';
    return ['reqMsg', 'numMsg', 'dupMsg'].includes(v) ? t(`health.${v}`) : v;
  };

  const fieldCls = (field) => `hp-field${errors[field] ? ' hp-field--err' : ''}`;

  return (
    <BasicLayout>
      <div className="hp-page">
        <div className="hp-head">
          <h2 className="hp-title">{t('health.pageTitle')}</h2>
          <Button type="primary" icon={<PlusOutlined />} onClick={openModal}>
            {t('health.create')}
          </Button>
        </div>
        <div className="hp-subtitle">{t('health.subtitle')}</div>

        {/* Фильтры перечня */}
        <div className="hp-toolbar">
          <span className="hp-flt-lbl">{t('health.fltPeriod')}</span>
          <Select
            value={fltPeriod || undefined}
            onChange={(v) => setFltPeriod(v || '')}
            allowClear
            placeholder={t('health.allPeriods')}
            style={{ width: 200 }}
          >
            {periodOptions.map((p) => (
              <Option key={p.key} value={p.key}>
                {p.label}
              </Option>
            ))}
          </Select>
          <span className="hp-flt-lbl">{t('health.fltIndicator')}</span>
          <Select
            value={fltIndicator || undefined}
            onChange={(v) => setFltIndicator(v || '')}
            allowClear
            placeholder={t('health.allIndicators')}
            style={{ width: 320 }}
          >
            {indicators.map((i) => (
              <Option key={i.code} value={i.code}>
                {lang === 'kk' ? i.name_kk : i.name_ru}
              </Option>
            ))}
          </Select>
        </div>

        {loadError ? (
          <Alert
            type="warning"
            showIcon
            className="hp-alert"
            message={t('health.loadError')}
            action={
              <Button size="small" onClick={load}>
                {t('health.retry')}
              </Button>
            }
          />
        ) : (
          <div className="hp-card">
            <Table
              className="hp-table"
              rowKey="id"
              columns={columns}
              dataSource={view}
              loading={loading}
              locale={{ emptyText: t('health.empty') }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total) =>
                  `${t('health.shown')} ${total} ${t('health.of')} ${rows.length}`,
              }}
            />
          </div>
        )}
      </div>

      {/* ===== Модальная форма (Табл.29). Своя разметка вместо antd Modal,
          чтобы точно повторить дизайн-систему макета (single design). ===== */}
      {modalOpen && (
        <div
          className="hp-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="hp-modal" role="dialog" aria-modal="true">
            <div className="hp-modal-head">
              <h3>{t('health.mTitle')}</h3>
              <button className="hp-modal-x" onClick={closeModal} aria-label="close">
                ✕
              </button>
            </div>

            <div className="hp-modal-body">
              {/* 1. Показатель */}
              <div className={fieldCls('indicator_code')}>
                <label>
                  {t('health.fIndicator')}
                  <span className="hp-req">*</span>
                </label>
                <Select
                  value={form.indicator_code}
                  onChange={onIndicatorChange}
                  placeholder={t('health.choose')}
                  style={{ width: '100%' }}
                  className="hp-select"
                >
                  {indicators.map((i) => (
                    <Option key={i.code} value={i.code}>
                      {lang === 'kk' ? i.name_kk : i.name_ru}
                    </Option>
                  ))}
                </Select>
                <div className="hp-hint">{t('health.hintIndicator')}</div>
                {errors.indicator_code && <div className="hp-msg">{errText('indicator_code')}</div>}
              </div>

              {/* 2. Тип периода — сегмент «Год/Квартал» */}
              <div className="hp-field">
                <label>
                  {t('health.fType')}
                  <span className="hp-req">*</span>
                </label>
                <div className="hp-seg">
                  <button
                    type="button"
                    className={form.period_type === 'year' ? 'on' : ''}
                    onClick={() => onTypeChange('year')}
                  >
                    {t('health.year')}
                  </button>
                  <button
                    type="button"
                    className={form.period_type === 'quarter' ? 'on' : ''}
                    onClick={() => onTypeChange('quarter')}
                  >
                    {t('health.quarter')}
                  </button>
                </div>
                <div className="hp-hint">{t('health.hintType')}</div>
              </div>

              {/* 3. Отчётный период — Год + (Квартал) */}
              <div className="hp-grp-cap">{t('health.grpPeriod')}</div>
              <div className="hp-row2">
                <div className={fieldCls('year')}>
                  <label>
                    {t('health.fYear')}
                    <span className="hp-req">*</span>
                  </label>
                  <Select
                    value={form.year}
                    onChange={(v) => {
                      patch({ year: v });
                      clearError('year');
                      clearError('dup');
                    }}
                    placeholder={t('health.choose')}
                    style={{ width: '100%' }}
                  >
                    {YEARS.map((y) => (
                      <Option key={y} value={y}>
                        {y}
                      </Option>
                    ))}
                  </Select>
                  {errors.year && <div className="hp-msg">{errText('year')}</div>}
                </div>

                {form.period_type === 'quarter' && (
                  <div className={fieldCls('quarter')}>
                    <label>
                      {t('health.fQuarter')}
                      <span className="hp-req">*</span>
                    </label>
                    <Select
                      value={form.quarter}
                      onChange={(v) => {
                        patch({ quarter: v });
                        clearError('quarter');
                        clearError('dup');
                      }}
                      placeholder={t('health.choose')}
                      style={{ width: '100%' }}
                    >
                      {[1, 2, 3, 4].map((q) => (
                        <Option key={q} value={q}>
                          {quarterLabel(q)}
                        </Option>
                      ))}
                    </Select>
                    {errors.quarter && <div className="hp-msg">{errText('quarter')}</div>}
                  </div>
                )}
              </div>

              {/* 4. Единица измерения (авто, read-only) */}
              <div className="hp-field">
                <label>
                  {t('health.fUnit')}
                  <span className="hp-req">*</span>
                </label>
                <Input
                  className="hp-ro"
                  readOnly
                  tabIndex={-1}
                  value={indUnit(form.indicator_code)}
                  placeholder={t('health.autoFill')}
                />
                <div className="hp-hint">{t('health.hintUnit')}</div>
              </div>

              {/* 5–6. План / Факт */}
              <div className="hp-row2">
                <div className={fieldCls('plan')}>
                  <label>
                    {t('health.fPlan')}
                    <span className="hp-req">*</span>
                  </label>
                  <InputNumber
                    value={form.plan === '' ? undefined : form.plan}
                    onChange={(v) => {
                      patch({ plan: v == null ? '' : v });
                      clearError('plan');
                    }}
                    min={0}
                    step={0.01}
                    stringMode
                    style={{ width: '100%' }}
                  />
                  <div className="hp-hint">{t('health.hintPlan')}</div>
                  {errors.plan && <div className="hp-msg">{errText('plan')}</div>}
                </div>

                <div className={fieldCls('fact')}>
                  <label>
                    {t('health.fFact')}
                    <span className="hp-req">*</span>
                  </label>
                  <InputNumber
                    value={form.fact === '' ? undefined : form.fact}
                    onChange={(v) => {
                      patch({ fact: v == null ? '' : v });
                      clearError('fact');
                    }}
                    min={0}
                    step={0.01}
                    stringMode
                    style={{ width: '100%' }}
                  />
                  <div className="hp-hint">{t('health.hintFact')}</div>
                  {errors.fact && <div className="hp-msg">{errText('fact')}</div>}
                </div>
              </div>

              {/* 7. Направление оценки (авто, read-only) */}
              <div className="hp-field">
                <label>
                  {t('health.fDir')}
                  <span className="hp-req">*</span>
                </label>
                <Input
                  className="hp-ro"
                  readOnly
                  tabIndex={-1}
                  value={dirLabel(form.indicator_code)}
                  placeholder={t('health.autoFill')}
                />
                <div className="hp-hint">{t('health.hintDir')}</div>
              </div>

              {/* 8. Примечание (необязательно, 500) */}
              <div className="hp-field">
                <label>{t('health.fNote')}</label>
                <TextArea
                  value={form.note}
                  onChange={(e) => patch({ note: e.target.value })}
                  maxLength={500}
                  showCount
                  rows={2}
                  placeholder={t('health.phNote')}
                />
                <div className="hp-hint">{t('health.optional')}</div>
              </div>

              {/* АС3 — дубль периода */}
              {errors.dup && <div className="hp-dup-msg">{errText('dup')}</div>}
            </div>

            <div className="hp-modal-foot">
              <Button onClick={closeModal} disabled={submitting}>
                {t('health.cancel')}
              </Button>
              <Button type="primary" onClick={save} loading={submitting}>
                {t('health.save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </BasicLayout>
  );
}
